import React, { useState, useEffect, useContext, createRef, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import NwContent from 'components/Layout/NwContent';
import NwBookLayout from 'components/Layout/NwBookLayout';
import useLocalStorageState from 'hooks/UseLocalStorageState';
import Playlist from 'components/Playlist/Playlist';
import { ARROW_UP_KEYCODE, ARROW_DOWN_KEYCODE } from '../../constants';
import { BookLayoutContext } from 'contexts/BookLayoutContext';
import PlaylistVScroll from 'components/Playlist/PlaylistVScroll';
import { ConfigContext } from 'contexts/ConfigContext';
import ModelNote from './ModelNote';
import { getStyledName } from 'utils/modelName';
import useDimensions from 'react-cool-dimensions';


const ModelWrapper = styled.div`
    position: relative;
    height: 100vh;
    overflow: hidden;

    .model-playlists-wrapper {
        height: 100vh;
        width:100%;
        position:absolute;
        right:-30px;
        overflow-y: scroll;
        overflow-x: hidden;
        padding-right: 30px;
        padding-top: ${props => props.$headerHeight};

        .model-playlist-wrapper {
            padding: 1.25rem 1rem 3.5rem 1rem;
            position: relative;

            .model-playlist-anchor {
                position: absolute;
                top: 0;
                left: 0;
            }
        }
    }
`
const NoteWrapper = styled.div`
    min-height: calc(100vh - ${props => props.$headerHeight}px);
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`

const Model = ({ models }) => {
    const { clientConfig } = useContext(ConfigContext);
    const { playlistViewer, } = clientConfig
    const { modelId } = useParams()
    const [model, setModel] = useState()
    const [activePlaylist, setActivePlaylist] = useState(0) //lo imposteremo per le funzioni di print e download
    const [readAlertModels, setReadAlertModels] = useLocalStorageState(`read-alert-models`, [])
    const [showAlert, setShowAlert] = useState(false)

    const scrollerRef = useRef(null)
    const { observe, width: modelBoxWidth } = useDimensions()
    

    let scrollHandler = null

    const modelFileName = useMemo(() => {
        if (model) {
            let modelNameStyle = null;
            if (clientConfig.model && clientConfig.model.nameStyle) {
                modelNameStyle = clientConfig.model.nameStyle;
            }
            const modelName = getStyledName(model, modelNameStyle)
            //fix modelName for filename char limitations
            return modelName.replace(/[^a-zA-Z0-9]/g, '_');
        }
        return ""
    }, [model, clientConfig.model]);

    const { headerHeight } = useContext(BookLayoutContext)

    useEffect(() => {
        if (model && model.note && !readAlertModels.find(modelId => modelId === model.ID)) {
            setTimeout(() => {
                setShowAlert(true);
            }, 1000);
        }
    }, [readAlertModels, model]);

    const handleChangeModel = (modelId) => {
        if (scrollHandler) {
            window.removeEventListener('scroll', scrollHandler)
        }
        const model = models.find(model => model.ID === modelId)
        setActivePlaylist(0)
        setShowAlert(false)
        setModel(model)
    };

    useEffect(() => {
        if (models.length > 0) {
            handleChangeModel(parseInt(modelId));
        }
    }, [models, modelId]);


    const [elRefs, setElRefs] = React.useState([])

    useEffect(() => {
        if (model && model.Playlists.length) {
            setElRefs((elRefs) =>
                Array(model.Playlists.length)
                    .fill()
                    .map((_, i) => elRefs[i] || createRef()),
            );
        }
        if (scrollerRef && scrollerRef.current) {
            scrollerRef.current.scrollTop = 0
        }
    }, [model, scrollerRef]);

    useEffect(() => {

        const handleKeyDown = (e) => {
            let gotopl = 0
            if (elRefs.length === 0 || gotopl >= elRefs.length) {
                return null
            }
            if (e.keyCode === ARROW_UP_KEYCODE) {
                e.preventDefault()
                gotopl = activePlaylist - 1
                if (gotopl < 0) {
                    gotopl = 0
                }
                const prevplaylistoffset = elRefs[gotopl].current.offsetTop
                scrollerRef.current.scrollTop = prevplaylistoffset - headerHeight
            } else if (e.keyCode === ARROW_DOWN_KEYCODE) {
                e.preventDefault()
                const npl = model.Playlists.length
                gotopl = activePlaylist + 1
                if (gotopl >= npl) {
                    gotopl = 0
                }
                const nextplaylistoffset = elRefs[gotopl].current.offsetTop
                scrollerRef.current.scrollTop = nextplaylistoffset - headerHeight
            }
        }

        scrollHandler = () => {
            let activeindex = 0;
            if (elRefs.length > 1) {
                for (let i = 0; i < elRefs.length; i++) {
                    if (elRefs[i] && elRefs[i].current) {
                        const scroll = scrollerRef.current.scrollTop + 600 //removed initial padding
                        const playlistoffset = elRefs[i].current.offsetTop
                        if (scroll > playlistoffset) {
                            activeindex = i;
                        }
                    } else {
                        activeindex = 0
                    }
                }
            }
            setActivePlaylist(activeindex)
        }

        if (elRefs.length > 1 && scrollerRef && scrollerRef.current) {
            scrollerRef.current.addEventListener('scroll', scrollHandler);
            window.addEventListener('keydown', handleKeyDown);
            return () => {
                scrollerRef.current.removeEventListener('scroll', scrollHandler)
                window.removeEventListener('keydown', handleKeyDown)
            }
        } else {
            setActivePlaylist(0)
        }
    }, [elRefs, scrollerRef, activePlaylist]);



    return (
        <NwBookLayout
            model={model}
            activePlaylist={activePlaylist}
        >
            <NwContent>
                {model &&
                    <ModelWrapper $headerHeight={`${headerHeight}px`} ref={observe}>
                        <div ref={scrollerRef} className="model-playlists-wrapper" id="modelPlaylistsWrapper">
                            {model.Playlists.map((playlist, index) => {
                                //gestire: singledocument (.DocumentPlaylists.length === 1), mobile (width <= 640 )
                                return (
                                    <div ref={elRefs[index]} className="model-playlist-wrapper" key={`playlist-${playlist.ID}`}>
                                        <div className="model-playlist-anchor" id={`playlist-${index}`}></div>
                                        {playlistViewer.style === 'vscroll'
                                            ?
                                            <PlaylistVScroll
                                                modelIDs={{ modelID: model.ModelID, packageModelID: model.ID }}
                                                modelFileName={modelFileName}
                                                playlist={playlist}
                                                isActive={activePlaylist === index}
                                            />
                                            :
                                            <Playlist
                                                modelFileName={modelFileName}
                                                playlist={playlist}
                                                isActive={activePlaylist === index}
                                                modelBoxWidth={modelBoxWidth}
                                            />
                                        }
                                    </div>
                                )
                            })}
                            {model.Note &&
                                <NoteWrapper $headerHeight={headerHeight} ref={elRefs[model.Playlists ? model.Playlists.length : 1]} className="model-playlist-wrapper model-note-wrapper" key={`playlist-note`}>
                                    <div className="model-playlist-anchor" id={`playlist-${model.Playlists.length}`}></div>
                                    <ModelNote modelNote={model.Note} />
                                </NoteWrapper>
                            }
                        </div>
                    </ModelWrapper>
                }
            </NwContent>
        </NwBookLayout>
    );
};

export default Model;